import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setLoginStatus, setUserInfo, setRes } from "../redux/dataSlice";
import {
  getNotificationData,
  deleteNotification,
  logout,
  platformLogout,
} from "../helperFunctions/apiFunction";
import { useAuthUserToken } from "../hooks/useAuthUserToken";
import toast from "react-hot-toast";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import NotificationSkeletonLoading from "./Notification/NotificationSkeletonLoading";
import { Spinner } from "@material-tailwind/react";
import SidebarLoading from "./Loaders/SidebarLoading";

const SideBar = () => {
  const { completeAuthData: authData, refreshToken, rbac } = useAuthUserToken();
  const [loading, setLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [getData, setData] = useState([]);
  const [notificationData, setNotificationData] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogout = () => {
    setLoading(true);
    if (!refreshToken) {
      console.error("Refresh token is not available");
      setLoading(false);
      return;
    }
    const { role, userEmail } = authData;
    const logoutFunc = role === "client" ? logout : platformLogout;
    logoutFunc(refreshToken, userEmail)
      .then((response) => {
        toast.success("Logout suceessfully", {
          className: "text-[12px]",
        });
        navigate("/login");
        dispatch(setLoginStatus({ status: false }));
        dispatch(setUserInfo(null));
        dispatch(setRes(null));
        localStorage.removeItem("access_token");
        localStorage.removeItem("rbac");
        localStorage.removeItem("user_info");
      })
      .catch((error) => {
        console.error("Error while logging out:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (Object.keys(rbac).length > 0) {
      setLoading(false);
    } else {
      toast.error(
        "Your role-based access is not yet defined. Please contact the administrator for assistance.",
        {
          className: "text-[12px]",
        }
      );
      setLoading(true);
    }
  }, [rbac]);

  useEffect(() => {
    const fetchNotificationData = async () => {
      setNotificationLoading(true);
      try {
        const data = await getNotificationData(refreshToken);
        if (data?.message === "Token has expired") {
          toast.error(data.message, {
            className: "text-[12px]",
          });
          handleLogout();
        } else {
          setData(data?.data || []);
        }
      } catch (error) {
        toast.error("An error occurred while fetching notifications.", {
          className: "text-[12px]",
        });
      } finally {
        setNotificationLoading(false);
      }
    };
    fetchNotificationData();
  }, [dispatch, notificationData, refreshToken]);

  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(refreshToken, notificationId);
      toast.success("Notification successfully deleted", {
        className: "text-[12px]",
      });
      setNotificationData((cur) => !cur);
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  return (
    <>
      <div className="sticky top-0 flex bg-gray-100 text-gray-900 z-50">
        <aside className="flex position-absolute h-screen w-16 flex-col items-center border-r border-gray-200 bg-white">
          <nav className="flex flex-1 flex-col gap-y-4 pt-10">
            {loading === null ? (
              <>
                {Array(6)
                  .fill()
                  .map((_, index) => (
                    <SidebarLoading key={index} />
                  ))}
              </>
            ) : loading === false ? (
              <>
                {authData.hasOwnProperty("clientId") ? (
                  <>
                    {/* Dashboard */}
                    {rbac?.cisoDash || rbac?.secDash ? (
                      <SidebarLink
                        imgSrc="/sidebar-icons/dashboard.png"
                        altText="dashboard-icon"
                        title="Dashboard"
                      >
                        <SideBarSpecificLink
                          path="analytics-dashboard"
                          title="Security"
                          isExist={"secDash"}
                        />
                        <SideBarSpecificLink
                          path="ciso-dashboard"
                          title="CISO"
                          isExist={"cisoDash"}
                        />
                      </SidebarLink>
                    ) : null}

                    {/* Inventory */}
                    {rbac?.inventoryAssestList ? (
                      <SidebarLink
                        imgSrc="/sidebar-icons/inventory.png"
                        altText="inventory-icon"
                        title="Inventory"
                      >
                        {/* <SideBarSpecificLink
                          path="inventory/assets"
                          title="Assets"
                          isExist={"inventoryAssestList"}
                        />
                        <SideBarSpecificLink
                          path="inventory/aws"
                          title="AWS"
                          isExist={"inventoryAWSList"}
                        />
                        <SideBarSpecificLink
                          path="inventory/azure"
                          title="Azure"
                          isExist={"inventoryAzureList"}
                        />
                        <SideBarSpecificLink
                          path="inventory/gcp"
                          title="GCP"
                          isExist={"inventoryGCPList"}
                        /> */}

                        <SideBarSpecificLink
                          path="inventory/assets"
                          title=" Integrations"
                          isExist={"inventoryAssestList"}
                        ></SideBarSpecificLink>
                        <SideBarSpecificLink
                          path="technology/page"
                          title="Technologies"
                          isExist={"inventoryAssestList"}
                        />
                        <SideBarSpecificLink
                          path="#"
                          title="Exposures"
                          isExist={"inventoryAssestList"}
                        ></SideBarSpecificLink>
                      </SidebarLink>
                    ) : null}

                    {/* Security Hub */}
                    {rbac?.scanList || rbac?.scanPluginList ? (
                      <SidebarLink
                        imgSrc="/sidebar-icons/security.png"
                        altText="security-icon"
                        title="Security Hub"
                      >
                        <SideBarSpecificLink
                          path="scan"
                          title="Scan"
                          isExist={"scanList"}
                        />
                        <SideBarSpecificLink
                          path="scanPolicy"
                          title="Policies"
                          isExist={"scanPluginList"}
                        />
                      </SidebarLink>
                    ) : null}

                    {/* Compliance */}
                    {rbac?.auditList ||
                    rbac?.evidenceList ||
                    rbac?.policyList ||
                    rbac?.peopleList ||
                    rbac?.vendorList ||
                    rbac?.riskList ||
                    rbac?.exceptionList ? (
                      <SidebarLink
                        imgSrc="/sidebar-icons/compliance.png"
                        altText="inventory-icon"
                        title="Compliance"
                      >
                        <SideBarSpecificLink
                          path="audit"
                          title="Compliance Audit"
                          isExist={"auditList"}
                        />
                        <SideBarSpecificLink
                          path="evidence"
                          title="Evidence Task"
                          isExist={"evidenceList"}
                        />
                        <SideBarSpecificLink
                          path="peopleManagement"
                          title="People Management"
                          isExist={"peopleList"}
                        />
                        <SideBarSpecificLink
                          path="policyManagement"
                          title="Policy Management"
                          isExist={"policyList"}
                        />
                        <SideBarSpecificLink
                          path="vendorManagement"
                          title="Vendor Management"
                          isExist={"vendorList"}
                        />
                        <SideBarSpecificLink
                          path="riskManagement"
                          title="Risk Management"
                          isExist={"riskList"}
                        />
                        <SideBarSpecificLink
                          path="listOfNonCompliances"
                          title="Execption Management"
                          isExist={"exceptionList"}
                        />
                      </SidebarLink>
                    ) : null}

                    {/* Settngs */}
                    {rbac?.organizationAdd ||
                    rbac?.userList ||
                    rbac?.roleList ||
                    rbac?.trainingList ? (
                      <SidebarLink
                        imgSrc="/sidebar-icons/settings.png"
                        altText="settings-icon"
                        title="Settings"
                      >
                        <SideBarSpecificLink
                          path="roleManagement"
                          title="Role Management"
                          isExist={"roleList"}
                        />
                        <SideBarSpecificLink
                          path="userManagement"
                          title="User Management"
                          isExist={"userList"}
                        />
                        <SideBarSpecificLink
                          path="trainingManagement"
                          title="Training Management"
                          isExist={"trainingList"}
                        />
                        <SideBarSpecificLink
                          path="organizationInfo"
                          title="Organization Info"
                          isExist={"organizationAdd"}
                        />
                      </SidebarLink>
                    ) : null}

                    {/* Settngs */}
                    {rbac?.serviceList ? (
                      <SidebarLink
                        imgSrc="/sidebar-icons/request.png"
                        altText="request-icon"
                        title="Service Request"
                      >
                        <SideBarSpecificLink
                          path="serviceRequests"
                          title="Requests"
                          isExist={"serviceList"}
                        />
                      </SidebarLink>
                    ) : null}

                    {/* Reviews and Approvals */}
                    {rbac?.exceptionApprovalList ||
                    rbac?.vendorReviewList ||
                    rbac?.policyApprovalList ? (
                      <SidebarLink
                        imgSrc="/sidebar-icons/reviewApprove.png"
                        altText="reviewApprove-icon"
                        title="Reviews and Approvals"
                      >
                        <SideBarSpecificLink
                          path="expceptionApprovals"
                          title="Exception Approval"
                          isExist={"exceptionApprovalList"}
                        />
                        <SideBarSpecificLink
                          path="vendorReview"
                          title="Vendor Reviews"
                          isExist={"vendorReviewList"}
                        />
                        <SideBarSpecificLink
                          path="policyApproval"
                          title="Policy Approvals"
                          isExist={"policyApprovalList"}
                        />
                      </SidebarLink>
                    ) : null}
                  </>
                ) : (
                  <>
                    {/* platform Dashboard */}
                    {rbac?.dash ? (
                      <SidebarLink
                        imgSrc="/sidebar-icons/dashboard.png"
                        altText="dashboard-icon"
                        title="Dashboard"
                      >
                        <SideBarSpecificLink
                          path="coming-soon"
                          title="View Dashboard"
                          isExist={"dash"}
                        />
                      </SidebarLink>
                    ) : null}

                    {/* platform client management */}
                    {rbac?.clientList ? (
                      <SidebarLink
                        imgSrc="/sidebar-icons/client1.png"
                        altText="client-icon"
                        title="Client Management"
                      >
                        <SideBarSpecificLink
                          path="client-management"
                          title="View All Clients"
                          isExist={"clientList"}
                        />
                      </SidebarLink>
                    ) : null}

                    {/* platform compliance program */}
                    {rbac?.programList ? (
                      <SidebarLink
                        imgSrc="/sidebar-icons/program.png"
                        altText="program-icon"
                        title="Compliance Program"
                      >
                        <SideBarSpecificLink
                          path="complianceProgram"
                          title="View All Programs"
                          isExist={"programList"}
                        />
                      </SidebarLink>
                    ) : null}

                    {/* platform settings */}
                    {rbac?.userList || rbac?.roleList ? (
                      <SidebarLink
                        imgSrc="/sidebar-icons/settings.png"
                        altText="setting-icon"
                        title="Settings"
                      >
                        <SideBarSpecificLink
                          path="user-management"
                          title="User Management"
                          isExist={"userList"}
                        />
                        <SideBarSpecificLink
                          path="role-management"
                          title="Role Management"
                          isExist={"roleList"}
                        />
                      </SidebarLink>
                    ) : null}
                  </>
                )}
              </>
            ) : (
              "Invalid Access!"
            )}
          </nav>

          <div className="flex flex-col items-center gap-y-4 py-10">
            {/* Notification */}
            {!rbac?.clientNoti || rbac?.platformNoti ? (
              <SidebarLink
                imgSrc="/sidebar-icons/notification.png"
                altText="notification-icon"
                title="Notification"
              >
                {notificationLoading === null ? (
                  <>
                    <NotificationSkeletonLoading />
                  </>
                ) : (
                  <>
                    <div className="flex flex-col gap-5 overflow-y-scroll h-72">
                      {getData &&
                        getData.map((item) => (
                          <div key={item._id}>
                            <div className="flex justify-between items-center gap-1 px-1">
                              <div>
                                <h5 className="text-gray-900 text-[16px] font-normal">
                                  {item.title}
                                </h5>
                                <p className="text-gray-700 text-[14px] font-extralight">
                                  {item.description}
                                </p>
                              </div>
                              <div className="p-1 rounded-full hover:bg-blue-gray-100 pointer">
                                <ClearIcon
                                  onClick={() => handleDelete(item._id)}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </SidebarLink>
            ) : null}

            {/* user profile */}
            {true ? (
              <SidebarLink
                imgSrc="/sidebar-icons/user.png"
                altText="user-icon"
                title={`Hi, ${authData?.userName}`}
              >
                {authData?.userName ? (
                  <>
                    <Link to="/edit-user" className="p-2 font-light">
                      <p>Edit Profile</p>
                    </Link>
                  </>
                ) : null}
                {authData?.userName ? (
                  <div className="flex justify-between items-center">
                    <button
                      onClick={handleLogout}
                      className="p-2 font-light flex items-start hover:underline hover:underline-offset-1 hover:text-light-blue-800"
                    >
                      <p>Logout</p>
                    </button>
                    {loading ? <Spinner className="h-4 w-4" /> : <></>}
                  </div>
                ) : null}
              </SidebarLink>
            ) : null}
          </div>
        </aside>
      </div>
    </>
  );
};

export default SideBar;

const SidebarLink = ({ to, imgSrc, altText, title, children }) => (
  <Link
    to={to || "#"}
    className="text-gray-400 group relative rounded-xl p-1 hover:bg-gray-50 border border-gray-50"
  >
    <img src={imgSrc} className="h-8 w-8" alt={altText} />
    <div className="absolute inset-y-0 left-[2.6rem] hidden items-center group-hover:flex">
      <div className="relative whitespace-nowrap rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 drop-shadow-lg">
        <div className="absolute -left-1 flex items-center">
          <div className="h-2 w-2 rotate-45 bg-white"></div>
        </div>
        <div className="flex flex-col px-1">
          <div className="p-1 border-b">
            <p>{title}</p>
          </div>
          {children}
        </div>
      </div>
    </div>
  </Link>
);

const SideBarSpecificLink = ({ path, title, isExist }) => {
  const { rbac } = useAuthUserToken();
  return (
    <>
      {rbac && isExist && rbac[isExist] ? (
        <Link to={`/${path}`} className="p-2 font-light">
          <p>{title}</p>
        </Link>
      ) : null}
    </>
  );
};
